import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { PawPrint, Warning, ArrowSquareOut, Heart, X, ShoppingBag } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import BeforeAfterSlider from "@/components/BeforeAfterSlider";

const CITIES = [
  { key: "all", label: "All Cities" },
  { key: "vegas", label: "Vegas · 702" },
  { key: "miami", label: "Miami · 305" },
  { key: "nyc", label: "NYC/PR · 917" },
];

export default function Rescue() {
  const [dogs, setDogs] = useState([]);
  const [city, setCity] = useState("all");
  const [adopt, setAdopt] = useState(null); // dog being adopted
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [hook, setHook] = useState(null);

  useEffect(() => { api.get("/rescues").then(({ data }) => setDogs(data)).catch(() => {}); }, []);

  const featured = useMemo(() => dogs.find((d) => d.featured) || dogs.find((d) => d.urgency === "urgent"), [dogs]);
  const list = useMemo(() => dogs.filter((d) => (city === "all" ? true : d.city === city)), [dogs, city]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/inquiries", {
        name: form.name, email: form.email, type: "adopt",
        subject: `Adoption interest — ${adopt.name} (${adopt.city.toUpperCase()})`,
        message: form.message || `I'd like to adopt ${adopt.name}.`,
        details: { dog: adopt.name, org: adopt.org, org_url: adopt.org_url, city: adopt.city },
      });
      toast.success(`Your interest in ${adopt.name} was sent 🐾`);
      setAdopt(null); setForm({ name: "", email: "", message: "" });
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  return (
    <div data-testid="rescue-page">
      {/* Hero */}
      <section className="relative overflow-hidden grain border-b border-primary/20">
        <img src="https://images.unsplash.com/photo-1754213310136-1b0e613ca63b?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600" alt="rescue" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-background via-background/85 to-background/50" />
        <div className="relative max-w-7xl mx-auto px-5 sm:px-8 py-28">
          <div className="flex items-center gap-2 mb-4"><PawPrint weight="fill" size={22} className="text-primary" /><span className="overline text-primary">#AdoptDontShop · The Guardian Wall</span></div>
          <h1 className="text-5xl sm:text-6xl lg:text-7xl font-modern font-semibold tracking-tighter leading-[0.9]">Wear one. <span className="aqua-text">Save one.</span></h1>
          <p className="mt-6 text-muted-foreground max-w-xl leading-relaxed">Every city collab is tied to real pit bull rescues on the ground. These are real dogs — some on kill lists right now. Meet them, share them, save them. In memory of Smiley, Beats &amp; King.</p>
        </div>
      </section>

      {/* Last-Chance spotlight */}
      {featured && (
        <section className="max-w-7xl mx-auto px-5 sm:px-8 py-16">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="grid md:grid-cols-2 gap-8 items-center rounded-2xl overflow-hidden border border-accent/40 bg-card neon-glow-pink" data-testid="rescue-featured">
            <div className="relative aspect-[4/3] md:aspect-auto md:h-full min-h-[320px]">
              {featured.before_image ? (
                <>
                  <BeforeAfterSlider before={featured.before_image} after={featured.after_image || featured.image}
                    onReveal={() => setHook(featured.name)} testid="rescue-slider-featured" />
                  <span className="absolute bottom-9 left-4 z-30 inline-flex items-center gap-1.5 overline px-3 py-1.5 rounded-full bg-accent text-accent-foreground pointer-events-none"><Warning weight="fill" size={14} /> Last-Chance Pit</span>
                </>
              ) : (
                <>
                  <img src={featured.image} alt={featured.name} className="h-full w-full object-cover" />
                  <span className="absolute top-4 left-4 inline-flex items-center gap-1.5 overline px-3 py-1.5 rounded-full bg-accent text-accent-foreground"><Warning weight="fill" size={14} /> Last-Chance Pit</span>
                </>
              )}
            </div>
            <div className="p-8">
              <p className="overline text-primary mb-2">{featured.city === "nyc" ? "NYC/PR · 917" : featured.city} · {featured.org}</p>
              <h2 className="text-4xl font-modern font-semibold tracking-tight mb-1">{featured.name}</h2>
              <p className="text-sm text-muted-foreground mb-4">{featured.breed} · {featured.age}</p>
              <p className="text-muted-foreground leading-relaxed mb-6">{featured.story}</p>
              <div className="flex flex-wrap gap-3">
                <button onClick={() => setAdopt(featured)} className="inline-flex items-center gap-2 bg-primary text-primary-foreground px-6 py-3 rounded-full font-modern font-medium hover:bg-primary/90 transition-colors" data-testid="rescue-adopt-featured"><Heart weight="fill" size={16} /> Start adoption</button>
                <a href={featured.org_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 border border-white/20 px-6 py-3 rounded-full font-modern hover:border-white/40 transition-colors">{featured.org} <ArrowSquareOut size={16} /></a>
              </div>
            </div>
          </motion.div>
        </section>
      )}

      {/* The wall */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 pb-24">
        <div className="flex gap-2 mb-8 overflow-x-auto no-scrollbar">
          {CITIES.map((c) => (
            <button key={c.key} onClick={() => setCity(c.key)} className={`overline whitespace-nowrap px-5 py-2.5 rounded-full border transition-colors ${city === c.key ? "bg-primary text-primary-foreground border-primary" : "border-white/15 text-muted-foreground hover:text-white"}`} data-testid={`rescue-city-${c.key}`}>{c.label}</button>
          ))}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="rescue-grid">
          {list.map((d, i) => (
            <motion.div key={d.name + i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.4, delay: (i % 3) * 0.06 }}
              className="rounded-xl overflow-hidden bg-card border border-white/5 hover:border-primary/30 card-hover" data-testid={`rescue-dog-${d.name.toLowerCase()}`}>
              <div className="relative aspect-[4/3]">
                {d.before_image ? (
                  <BeforeAfterSlider before={d.before_image} after={d.after_image || d.image} showLabels={false}
                    onReveal={() => setHook(d.name)} testid={`rescue-slider-${d.name.toLowerCase()}`} />
                ) : (
                  <img src={d.image} alt={d.name} className="h-full w-full object-cover" />
                )}
                {d.urgency === "urgent" && <span className="absolute top-3 left-3 z-30 inline-flex items-center gap-1 overline px-2.5 py-1 rounded-full bg-accent text-accent-foreground pointer-events-none"><Warning weight="fill" size={12} /> Urgent</span>}
                <span className="absolute top-3 right-3 z-30 overline px-2.5 py-1 rounded-full bg-black/60 backdrop-blur text-primary uppercase pointer-events-none">{d.city === "nyc" ? "917" : d.city === "vegas" ? "702" : "305"}</span>
              </div>
              <div className="p-5">
                <h3 className="text-xl font-modern font-semibold">{d.name}</h3>
                <p className="text-xs text-muted-foreground mb-2">{d.breed} · {d.age} · {d.org}</p>
                <p className="text-sm text-muted-foreground leading-relaxed line-clamp-3 mb-4">{d.story}</p>
                <div className="flex gap-2">
                  <button onClick={() => setAdopt(d)} className="flex-1 inline-flex items-center justify-center gap-1.5 overline py-2.5 rounded-full bg-primary text-primary-foreground hover:bg-primary/90 transition-colors" data-testid={`rescue-adopt-${d.name.toLowerCase()}`}><Heart weight="fill" size={14} /> Adopt</button>
                  <a href={d.org_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center px-3 rounded-full border border-white/20 hover:border-white/40 transition-colors" aria-label={`Open ${d.org}`}><ArrowSquareOut size={16} /></a>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Adoption modal */}
      {adopt && (
        <div className="fixed inset-0 z-[60] grid place-items-center p-5 bg-black/70 backdrop-blur-sm" onClick={() => setAdopt(null)} data-testid="rescue-modal">
          <div className="w-full max-w-md bg-popover border border-primary/30 rounded-2xl p-6 neon-glow" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <div><p className="overline text-primary">Start adoption</p><h3 className="text-2xl font-modern font-semibold">{adopt.name}</h3></div>
              <button onClick={() => setAdopt(null)} className="text-muted-foreground hover:text-white" data-testid="rescue-modal-close"><X size={22} /></button>
            </div>
            <p className="text-sm text-muted-foreground mb-4">We'll connect you with <span className="text-white">{adopt.org}</span> to start {adopt.name}'s adoption.</p>
            <form onSubmit={submit} className="space-y-3" data-testid="rescue-adopt-form">
              <input required placeholder="Your name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full bg-transparent border border-white/20 rounded-lg px-4 py-3 outline-none focus:border-primary transition-colors" data-testid="rescue-name" />
              <input required type="email" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="w-full bg-transparent border border-white/20 rounded-lg px-4 py-3 outline-none focus:border-primary transition-colors" data-testid="rescue-email" />
              <textarea rows={3} placeholder={`Why you'd be a great home for ${adopt.name}…`} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="w-full bg-transparent border border-white/20 rounded-lg px-4 py-3 outline-none focus:border-primary transition-colors resize-none" data-testid="rescue-message" />
              <button disabled={loading} className="w-full bg-primary text-primary-foreground py-3 rounded-full font-modern font-medium hover:bg-primary/90 transition-colors disabled:opacity-60" data-testid="rescue-submit">{loading ? "Sending…" : "Send adoption interest"}</button>
            </form>
          </div>
        </div>
      )}
      {/* Transformation "hook" popup */}
      {hook && (
        <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
          className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[70] w-[calc(100%-2rem)] max-w-md rounded-2xl border border-primary/40 bg-[#0b0b0b]/95 backdrop-blur p-5 neon-glow-pink" data-testid="rescue-hook-popup">
          <button onClick={() => setHook(null)} className="absolute top-3 right-3 text-muted-foreground hover:text-white" data-testid="rescue-hook-close"><X size={18} /></button>
          <p className="overline text-primary mb-1 inline-flex items-center gap-1.5"><Heart weight="fill" size={13} /> {hook}'s story</p>
          <h3 className="font-display font-black uppercase text-2xl leading-tight mb-3">Help us write more stories like this.</h3>
          <p className="text-sm text-muted-foreground mb-4">Every piece you wear pulls another pit off the streets and into the pack.</p>
          <Link to="/shop" onClick={() => setHook(null)} className="inline-flex items-center gap-2 bg-primary text-primary-foreground px-6 py-3 rounded-[10px] font-modern font-medium hover:bg-primary/90 transition-colors" data-testid="rescue-hook-shop">
            <ShoppingBag weight="fill" size={17} /> Shop the Cause
          </Link>
        </motion.div>
      )}
    </div>
  );
}
